/**
 * packages/server/src/auditor.ts
 *
 * 사용자 함수(gencow/) 번들의 import를 분석해
 * 프로덕션(Firecracker microVM)에서 차단될 모듈 사용 여부를 검사.
 *
 * 또한, 등록된 query/mutation의 public/private 상태를 분석하여
 * 보안 감사 리포트를 출력 (Secure by Default).
 *
 * 서버 시작 시 함수 로드 전에 실행된다.
 * 위반 발견 시 서버 시작을 중단하고 명확한 오류 메시지를 출력.
 */
import * as esbuild from "esbuild";

// ─── 금지 모듈 목록 ────────────────────────────────────────
// ESLint의 FORBIDDEN_MODULES와 동기화 유지.
// (eslint.config.mjs의 목록과 일치시킬 것)
//
// 차단 유지 이유:
// - child_process: 임의 명령 실행 → 샌드박스 탈출 위험
// - vm: 동적 코드 실행 → 코드 인젝션 위험
// - os: 시스템 정보 노출 (CPU, 메모리, 네트워크 인터페이스)
// - worker_threads/cluster: 리소스 독점 + 프로세스 관리 우회
// - dgram: UDP 소켓 → cowbox Landlock 우회 가능성
// - v8: V8 엔진 내부 접근 → 보안 경계 우회
const FORBIDDEN_MODULES = new Set([
  "child_process",
  "node:child_process",
  "dgram",
  "node:dgram",
  "os",
  "node:os",
  "cluster",
  "node:cluster",
  "worker_threads",
  "node:worker_threads",
  "v8",
  "node:v8",
  "vm",
  "node:vm",
]);

export interface AuditResult {
  passed: boolean;
  violations: { module: string; importedFrom: string }[];
}

/**
 * 사용자 함수 경로를 받아 금지 모듈 사용 여부를 분석.
 * esbuild의 metafile을 활용해 번들 내 모든 import를 추출.
 */
export async function auditFunctions(functionsPath: string): Promise<AuditResult> {
  const violations: { module: string; importedFrom: string }[] = [];

  try {
    // esbuild로 번들 분석 (실제 번들링은 하지 않고 메타데이터만 수집)
    const result = await esbuild.build({
      entryPoints: [functionsPath],
      bundle: true,
      write: false, // 디스크에 쓰지 않음
      metafile: true, // import 그래프 수집
      platform: "node",
      format: "esm",
      // 외부 패키지 (node_modules)는 번들에서 제외하되 import 목록은 수집
      packages: "external",
      logLevel: "silent", // esbuild 자체 로그 숨김
    });

    if (result.metafile) {
      // 모든 input 파일에서 import 목록 추출
      for (const [filePath, fileData] of Object.entries(result.metafile.inputs) as any[]) {
        for (const imported of (fileData as any).imports) {
          const moduleName = imported.path;

          // node: 프로토콜 모듈 전체 차단
          if (moduleName.startsWith("node:") && !isAllowedNodeModule(moduleName)) {
            violations.push({ module: moduleName, importedFrom: filePath });
            continue;
          }

          // 금지 목록 검사
          if (FORBIDDEN_MODULES.has(moduleName)) {
            violations.push({ module: moduleName, importedFrom: filePath });
          }
        }
      }
    }
  } catch (err) {
    // 번들 분석 실패 시 경고만 출력하고 계속 (함수 로딩을 막지 않음)
    console.warn("[auditor] Module audit skipped — could not analyze bundle:", (err as Error).message);
    return { passed: true, violations: [] };
  }

  return {
    passed: violations.length === 0,
    violations,
  };
}

// ─── 허용된 node: 모듈 ──────────────────────────────────────
// crypto, path, url, buffer 등 순수 유틸리티는 허용.
// http, https, net, tls 등은 서드파티 패키지 호환을 위해 허용.
// 네트워크 격리는 cowbox Landlock TCP 규칙으로 처리.
//
// ⛔ fs 계열 차단:
//   - 사용자 코드에서 직접 fs 접근을 원천 차단
//   - cowbox Landlock Phase 2에서 per-app 디렉토리만 허용하지만,
//     빌드타임에 1차 방어하여 보안 계층 강화
//   - 프레임워크(core/storage.ts)는 auditor 대상 아님 → 영향 없음
//     (esbuild의 packages: "external" 설정으로 node_modules 제외)
//   - 파일 접근은 ctx.storage API를 사용
const ALLOWED_NODE_MODULES = new Set([
  "node:crypto",
  "node:path",
  "node:url",
  "node:buffer",
  "node:stream",
  "node:events",
  "node:util",
  "node:assert",
  "node:querystring",
  "node:string_decoder",
  "node:timers",
  "node:timers/promises",
  "node:zlib",
  // 서드파티 패키지 호환을 위해 허용 (cowbox Landlock TCP가 보안 담당)
  "node:http",
  "node:https",
  "node:http2",
  "node:net",
  "node:tls",
  "node:readline",
  // non-prefixed equivalents
  "crypto",
  "path",
  "url",
  "buffer",
  "stream",
  "events",
  "util",
  "assert",
  "querystring",
  "string_decoder",
  "timers",
  "zlib",
  "http",
  "https",
  "http2",
  "net",
  "tls",
  "readline",
]);

function isAllowedNodeModule(moduleName: string): boolean {
  return ALLOWED_NODE_MODULES.has(moduleName);
}

/**
 * 감사 결과를 사람이 읽기 쉬운 형식으로 출력
 */
export function printAuditReport(result: AuditResult, functionsPath: string): void {
  if (result.passed) {
    console.log(`[auditor] ✅ Module audit passed — no forbidden APIs detected`);
    return;
  }

  const banner = `
╔══════════════════════════════════════════════════════════════╗
║  ⛔ GENCOW AUDIT FAILED — Production Constraint Violation    ║
╚══════════════════════════════════════════════════════════════╝`;

  console.error(banner);
  console.error(`\n  Functions: ${functionsPath}`);
  console.error(`\n  The following modules are BLOCKED in production (Firecracker microVM)`);
  console.error(`  but are used in your gencow/ functions:\n`);

  for (const { module, importedFrom } of result.violations) {
    console.error(`    ✗  import "${module}"`);
    console.error(`       in ${importedFrom}`);
  }

  console.error(`\n  Fix: Replace direct module access with ctx API:`);
  console.error(`    ctx.db         — database queries (Drizzle)`);
  console.error(`    ctx.storage    — file storage`);
  console.error(`    ctx.scheduler  — background jobs`);
  console.error(`    ctx.auth       — authentication\n`);
}

// ─── 보안 감사: Secure by Default ────────────────────────

export interface SecurityAuditResult {
  publicQueries: string[];
  publicMutations: string[];
  protectedQueries: string[];
  protectedMutations: string[];
}

/**
 * 등록된 함수들의 public/private 상태를 분석.
 * 서버 부팅 시 호출하여 공개 함수 목록을 경고.
 */
export function auditSecurity(
  queryNames: string[],
  queryDefs: { key: string; isPublic: boolean }[],
  mutations: { name: string; isPublic: boolean }[],
): SecurityAuditResult {
  const publicQueries = queryDefs.filter((d) => d.isPublic).map((d) => d.key);
  const publicMutations = mutations.filter((m) => m.isPublic).map((m) => m.name);
  const protectedQueries = queryDefs.filter((d) => !d.isPublic).map((d) => d.key);
  const protectedMutations = mutations.filter((m) => !m.isPublic).map((m) => m.name);

  return { publicQueries, publicMutations, protectedQueries, protectedMutations };
}

/**
 * 보안 감사 결과를 콘솔에 출력.
 * deploy 시 공개 함수가 있으면 경고 배너 표시.
 */
export function printSecurityAuditReport(result: SecurityAuditResult): void {
  const totalPublic = result.publicQueries.length + result.publicMutations.length;
  const totalProtected = result.protectedQueries.length + result.protectedMutations.length;

  if (totalPublic > 0) {
    console.log(``);
    console.log(`[auditor] ⚠️  SECURITY AUDIT — ${totalPublic} public function(s) detected:`);
    if (result.publicQueries.length > 0) {
      console.log(`[auditor]   📖 queries:   ${result.publicQueries.join(", ")}`);
    }
    if (result.publicMutations.length > 0) {
      console.log(`[auditor]   ✏️  mutations: ${result.publicMutations.join(", ")}`);
    }
    console.log(`[auditor]   → These functions are accessible WITHOUT authentication.`);
    console.log(`[auditor]   → Remove { public: true } if auth is intended.`);
  }

  console.log(`[auditor] 🔒 ${totalProtected} function(s) protected (auth required)`);
}

/**
 * 사용자 스키마 파일을 읽어 보안 anti-pattern을 감지.
 * 경고만 출력하고 서버 시작은 차단하지 않음 (lint처럼 동작).
 *
 * 감지 항목:
 *   1. userId 컬럼 없는 pgTable (시스템 테이블 제외)
 *   2. userId에 .references() 없음
 *   3. userId가 nullable (.notNull() 없음)
 *   4. [NEW] pgTable 사용 시 RLS 누락 권고 (TODO)
 */
export function auditSchemaPatterns(schemaPath: string): void {
  try {
    const fs = require("fs");
    const path = require("path");
    const content = fs.readFileSync(schemaPath, "utf-8") as string;
    const fileName = path.basename(schemaPath);

    // auth-schema.ts는 better-auth가 생성 — pgTable 사용이 정상
    if (fileName === "auth-schema.ts") return;

    const warnings: string[] = [];

    // [NEW] pgTable import 감지 및 권고 삭제 (이제 pgTable이 표준)

    // pgTable 정의 블록 추출
    const tableRegex =
      /export\s+const\s+(\w+)\s*=\s*pgTable\s*\(\s*"([^"]+)"\s*,\s*\{([^}]+(?:\{[^}]*\}[^}]*)*)\}/g;

    // 시스템/인프라 테이블 (userId 체크 제외)
    const SYSTEM_TABLES = new Set([
      "user",
      "session",
      "account",
      "verification", // auth
      "_system_files", // storage 시스템 테이블 (nullable userId 허용)
      "_system_settings",
      "usage_snapshots",
      "credits",
    ]);

    let match;
    while ((match = tableRegex.exec(content)) !== null) {
      const [, varName, tableName, body] = match;

      // 시스템 테이블 스킵
      if (SYSTEM_TABLES.has(tableName)) continue;

      const hasUserId = /user_id|userId/.test(body);

      // userId가 있는 경우 pgPolicy/RLS가 적용되었는지 검사하면 좋으나
      // 현재는 구조 제약이 풀리므로 생략 (rls.ts에서 확인)

      if (!hasUserId) {
        warnings.push(`  ⚠️  "${tableName}" — userId 컬럼 없음 → 사용자별 데이터 격리 불가`);
        continue;
      }

      const hasReferences = /\.references\s*\(/.test(body);
      if (!hasReferences) {
        warnings.push(`  ⚠️  "${tableName}" — userId에 .references() 없음 → FK 무결성 미보장`);
      }

      const userIdLine = body.split("\n").find((l) => /user_id|userId/.test(l)) || "";
      if (!userIdLine.includes("notNull")) {
        warnings.push(`  💡 "${tableName}" — userId가 nullable → 소유자 없는 데이터 발생 가능`);
      }
    }

    if (warnings.length > 0) {
      console.log(`\n[auditor] 🔍 스키마 보안 패턴 점검 (${fileName}):`);
      for (const w of warnings) {
        console.log(`[auditor] ${w}`);
      }
      console.log(`[auditor]   → 📄 gencow/SECURITY.md 참고`);
      console.log(``);
    }
  } catch {
    // 스키마 파일을 못 읽으면 조용히 무시 (optional 기능)
  }
}

// ─── Self-Fetch 감지 ────────────────────────────────────

export interface SelfFetchAuditResult {
  usages: { file: string; line: number; snippet: string }[];
}

/**
 * gencow/ 폴더 내에서 fetch("/api/mutation") 또는 fetch("/api/query") self-fetch 패턴을 감지.
 * 같은 서버의 함수를 fetch로 호출하는 것은 불필요한 네트워크 우회이므로 경고한다.
 *
 * 감지 패턴:
 *   - fetch("/api/mutation"), fetch("/api/query")
 *   - fetch(`${baseUrl}/api/mutation`), fetch(`${GENCOW_INTERNAL_URL}/api/query`)
 *   - fetch(url + "/api/mutation")
 */
export function auditSelfFetch(functionsDir: string): SelfFetchAuditResult {
  const usages: { file: string; line: number; snippet: string }[] = [];

  try {
    const fs = require("fs");
    const path = require("path");

    const files = findTsFiles(functionsDir);
    // /api/query 또는 /api/mutation 을 fetch 호출 내에서 참조하는 패턴
    const selfFetchPattern = /\/api\/(query|mutation)/;

    for (const file of files) {
      const content = fs.readFileSync(file, "utf-8") as string;
      const lines = content.split("\n");
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        // 주석 라인은 스킵
        const trimmed = line.trim();
        if (trimmed.startsWith("//") || trimmed.startsWith("*")) continue;

        // fetch() 호출 내에서 /api/query 또는 /api/mutation 참조 감지
        if (line.includes("fetch") && selfFetchPattern.test(line)) {
          usages.push({
            file: path.relative(functionsDir, file),
            line: i + 1,
            snippet: trimmed,
          });
        }
      }
    }
  } catch {
    // 디렉터리를 못 읽으면 조용히 무시
  }

  return { usages };
}

/**
 * Self-fetch 감사 결과를 콘솔에 출력.
 */
export function printSelfFetchReport(result: SelfFetchAuditResult): void {
  if (result.usages.length === 0) return;

  console.log(`\n[auditor] ⚠️  Self-fetch detected in ${result.usages.length} location(s):`);
  for (const u of result.usages) {
    console.log(`[auditor]   ${u.file}:${u.line} → ${u.snippet}`);
  }
  console.log(`[auditor]   → 같은 서버의 함수는 직접 import하세요:`);
  console.log(`[auditor]     import { fetchNews } from "./crawler";`);
  console.log(`[auditor]     const result = await fetchNews.handler(ctx, { keyword });`);
  console.log(`[auditor]   → fetch("/api/mutation") 패턴은 불필요한 네트워크 우회입니다.\n`);
}

// ─── 프론트엔드 Anti-Pattern 감지 ─────────────────────────

export interface FrontendAntiPatternResult {
  issues: { file: string; reason: string }[];
}

/**
 * 프론트엔드 소스(src/)에서 커스텀 API 래퍼 파일 + fetch 직접 호출을 감지.
 * AI가 gencow/api.ts (자동 생성)를 무시하고 독자 API 클라이언트를 만드는 패턴을 경고.
 *
 * 감지 대상:
 *   - src/lib/api.ts, src/api.ts 등 커스텀 API 래퍼 파일
 *   - 해당 파일 내 fetch("/api/query") 또는 fetch("/api/mutation") 패턴
 */
export function auditFrontendAntiPatterns(projectDir: string): FrontendAntiPatternResult {
  const issues: { file: string; reason: string }[] = [];

  try {
    const fs = require("fs");
    const path = require("path");

    const srcDir = path.join(projectDir, "src");
    if (!fs.existsSync(srcDir)) return { issues };

    // 의심 파일명 패턴 — 커스텀 API 래퍼 후보
    const SUSPECT_NAMES = new Set([
      "api.ts",
      "api.tsx",
      "api-client.ts",
      "api-client.tsx",
      "rpc.ts",
      "rpc.tsx",
      "client.ts",
      "client.tsx",
    ]);

    const selfFetchPattern = /\/api\/(query|mutation)/;

    const allFiles = findTsFiles(srcDir);
    for (const file of allFiles) {
      const basename = path.basename(file);
      const relPath = path.relative(projectDir, file);

      // gencow/ 디렉토리 내 파일은 스킵 (자동 생성 파일)
      if (relPath.startsWith("gencow/") || relPath.startsWith("gencow\\")) continue;

      if (SUSPECT_NAMES.has(basename)) {
        const content = fs.readFileSync(file, "utf-8") as string;
        if (selfFetchPattern.test(content)) {
          issues.push({
            file: relPath,
            reason: `fetch("/api/query" 또는 "/api/mutation") 직접 호출 감지 — 이 파일을 삭제하세요`,
          });
        }
      }
    }
  } catch {
    // 프론트엔드 소스를 못 읽으면 조용히 무시
  }

  return { issues };
}

/**
 * 프론트엔드 anti-pattern 감사 결과 출력.
 */
export function printFrontendAntiPatternReport(result: FrontendAntiPatternResult): void {
  if (result.issues.length === 0) return;

  console.log(`\n[auditor] ⚠️  Custom API client detected:`);
  for (const issue of result.issues) {
    console.log(`[auditor]   ${issue.file} — ${issue.reason}`);
  }
  console.log(`[auditor]   → gencow/api.ts (자동 생성)와 useQuery/useMutation을 사용하세요`);
  console.log(`[auditor]   → 📄 README.md "❌ 절대 하지 마세요" 섹션 참조\n`);
}

// ─── Workflow Shadowing 감지 ──────────────────────────────

const WORKFLOW_SHADOW_FILENAMES = new Set(["workflow.ts", "workflow.tsx", "workflow.js", "workflow.jsx"]);

const WORKFLOW_SHADOW_RUNTIME_MARKERS = [
  "__gencow_workflowRegistry",
  "WORKFLOW_RESUME_ACTION_PREFIX",
  "registerWorkflowsApi(",
  'query("workflows.get"',
  'query("workflows.list"',
  'mutation("workflows.signal"',
  "getWorkflowResumeActionName(",
];

const WORKFLOW_RELATIVE_IMPORT_PATTERN = /from\s+["'](?:\.\.\/|\.\/)workflow(?:\.(?:[cm]?[jt]sx?))?["']/;

export interface WorkflowShadowingAuditResult {
  passed: boolean;
  issues: { file: string; line: number; snippet: string; type: "shadow-file" | "relative-import" }[];
}

/**
 * gencow/ 내부에서 framework workflow runtime을 포크하거나
 * ./workflow 상대 import로 shadowing 하는 패턴을 감지한다.
 *
 * 기본 동작은 경고이며, GENCOW_STRICT=true 에서만 startup abort 대상으로 사용한다.
 */
export function auditWorkflowShadowing(functionsDir: string): WorkflowShadowingAuditResult {
  const issues: WorkflowShadowingAuditResult["issues"] = [];

  try {
    const fs = require("fs");
    const path = require("path");

    const files = findTsFiles(functionsDir);
    for (const file of files) {
      const content = fs.readFileSync(file, "utf-8") as string;
      const relPath = path.relative(functionsDir, file);
      const basename = path.basename(file);

      if (WORKFLOW_SHADOW_FILENAMES.has(basename)) {
        const markers = WORKFLOW_SHADOW_RUNTIME_MARKERS.filter((marker) => content.includes(marker));
        if (markers.length > 0) {
          issues.push({
            file: relPath,
            line: 1,
            snippet: `framework runtime markers: ${markers.slice(0, 3).join(", ")}`,
            type: "shadow-file",
          });
        }
      }

      const lines = content.split("\n");
      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (
          trimmed.startsWith("//") ||
          trimmed.startsWith("*") ||
          trimmed.startsWith("/*") ||
          trimmed.startsWith("*/")
        ) {
          continue;
        }

        if (WORKFLOW_RELATIVE_IMPORT_PATTERN.test(trimmed)) {
          issues.push({
            file: relPath,
            line: i + 1,
            snippet: trimmed.slice(0, 140),
            type: "relative-import",
          });
        }
      }
    }
  } catch {
    // 디렉터리를 못 읽으면 조용히 무시
  }

  return {
    passed: issues.length === 0,
    issues,
  };
}

export function printWorkflowShadowingReport(result: WorkflowShadowingAuditResult): void {
  if (result.issues.length === 0) return;

  const typeLabels: Record<WorkflowShadowingAuditResult["issues"][number]["type"], string> = {
    "shadow-file": "🧬 로컬 workflow runtime 포크",
    "relative-import": "🔁 상대 workflow import",
  };

  console.log(`\n[auditor] ⚠️  Workflow shadowing detected (${result.issues.length}건):`);
  for (const issue of result.issues) {
    const label = typeLabels[issue.type] || "⚠️  기타";
    const loc = issue.line > 0 ? `${issue.file}:${issue.line}` : issue.file;
    console.log(`[auditor]   ${label} — ${loc}`);
    console.log(`[auditor]     ${issue.snippet}`);
  }
  console.log(`[auditor]   → workflow는 반드시 import { workflow } from "@gencow/core" 를 사용하세요.`);
  console.log(`[auditor]   → gencow/workflow.ts 같은 shadow file은 지원하지 않습니다.`);
  console.log(`[auditor]   → helper가 필요하면 다른 파일명으로 만들고 core workflow를 감싸세요.\n`);
}

// ─── 유틸리티 ───────────────────────────────────────────

/** 디렉터리에서 .ts 파일 재귀 검색 */
function findTsFiles(dir: string): string[] {
  const fs = require("fs");
  const path = require("path");
  const results: string[] = [];

  try {
    const entries = fs.readdirSync(dir, { withFileTypes: true });
    for (const entry of entries) {
      const fullPath = path.join(dir, entry.name);
      if (entry.isDirectory() && entry.name !== "node_modules" && !entry.name.startsWith(".")) {
        results.push(...findTsFiles(fullPath));
      } else if (entry.isFile() && (entry.name.endsWith(".ts") || entry.name.endsWith(".tsx"))) {
        results.push(fullPath);
      }
    }
  } catch {
    // ignore
  }

  return results;
}

// ─── AI 우회 패턴 감지 (test041→043 포스트모템 대응) ─────

export interface AIBypassAuditResult {
  issues: { file: string; line: number; snippet: string; type: string }[];
}

/**
 * gencow/ 폴더 내에서 AI 프록시를 우회하는 패턴을 감지.
 *
 * test041→test042→test043에서 반복 발견된 패턴:
 *   1. fetch("https://api.openai.com/...") — OpenAI API 직접 호출
 *   2. openai-direct.ts, ai-wrapper.ts 등 별도 AI 래퍼 파일 생성
 *   3. new OpenAI({ apiKey: ... }) — openai 패키지 직접 사용
 *   4. callGPT(), callOpenAI() 같은 인라인 AI 호출 함수
 *
 * 이러한 패턴이 발견되면 경고를 출력하여:
 *   - import { ai } from "./ai" 사용을 안내
 *   - ai.ts 수정/우회 금지를 전달
 */
export function auditAIBypass(functionsDir: string): AIBypassAuditResult {
  const issues: { file: string; line: number; snippet: string; type: string }[] = [];

  try {
    const fs = require("fs");
    const path = require("path");

    const files = findTsFiles(functionsDir);

    // 의심 파일명 — AI 래퍼 파일 후보 (ai.ts 자체는 제외)
    const SUSPECT_FILENAMES = new Set([
      "openai-direct.ts",
      "openai-direct.tsx",
      "ai-wrapper.ts",
      "ai-wrapper.tsx",
      "ai-helper.ts",
      "ai-helper.tsx",
      "ai-client.ts",
      "ai-client.tsx",
      "gpt.ts",
      "gpt.tsx",
      "openai.ts",
      "openai.tsx",
      "llm.ts",
      "llm.tsx",
    ]);

    // 코드 내 AI 직접 호출 패턴
    const DIRECT_OPENAI_FETCH = /fetch\s*\(\s*[`"']https?:\/\/api\.openai\.com/;
    const DIRECT_OPENAI_CONSTRUCTOR = /new\s+OpenAI\s*\(/;
    const INLINE_AI_FUNC =
      /(?:async\s+)?function\s+(?:callGPT|callOpenAI|callAI|fetchGPT|aiCall|directAI)\s*\(/;
    const OPENAI_IMPORT = /(?:import|require)\s*\(?.*['"]openai['"]/;

    for (const file of files) {
      const basename = path.basename(file);
      const relPath = path.relative(functionsDir, file);

      // ai.ts 자체는 제외
      if (basename === "ai.ts") continue;

      // 1. 의심 파일명 감지
      if (SUSPECT_FILENAMES.has(basename)) {
        issues.push({
          file: relPath,
          line: 0,
          snippet: `파일명 "${basename}" — AI 우회 래퍼 의심`,
          type: "suspect-file",
        });
      }

      const content = fs.readFileSync(file, "utf-8") as string;
      const lines = content.split("\n");

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        // 주석 스킵
        if (trimmed.startsWith("//") || trimmed.startsWith("*") || trimmed.startsWith("/*")) continue;

        // 2. OpenAI API 직접 fetch
        if (DIRECT_OPENAI_FETCH.test(line)) {
          issues.push({
            file: relPath,
            line: i + 1,
            snippet: trimmed.slice(0, 100),
            type: "direct-fetch",
          });
        }

        // 3. new OpenAI() 생성자
        if (DIRECT_OPENAI_CONSTRUCTOR.test(line)) {
          issues.push({
            file: relPath,
            line: i + 1,
            snippet: trimmed.slice(0, 100),
            type: "openai-constructor",
          });
        }

        // 4. callGPT 등 인라인 AI 함수 정의
        if (INLINE_AI_FUNC.test(line)) {
          issues.push({
            file: relPath,
            line: i + 1,
            snippet: trimmed.slice(0, 100),
            type: "inline-ai-func",
          });
        }

        // 5. import OpenAI from "openai" (직접 패키지 사용)
        if (OPENAI_IMPORT.test(line)) {
          issues.push({
            file: relPath,
            line: i + 1,
            snippet: trimmed.slice(0, 100),
            type: "openai-import",
          });
        }
      }
    }
  } catch {
    // 디렉터리를 못 읽으면 조용히 무시
  }

  return { issues };
}

/**
 * AI 우회 패턴 감사 결과를 콘솔에 출력.
 */
export function printAIBypassReport(result: AIBypassAuditResult): void {
  if (result.issues.length === 0) return;

  const typeLabels: Record<string, string> = {
    "suspect-file": "🗂️  AI 우회 래퍼 파일",
    "direct-fetch": "🔴 OpenAI API 직접 호출",
    "openai-constructor": "🔴 OpenAI 생성자 직접 사용",
    "inline-ai-func": "⚠️  인라인 AI 함수 정의",
    "openai-import": "⚠️  openai 패키지 직접 import",
  };

  console.log(`\n[auditor] 🤖 AI 우회 패턴 감지 (${result.issues.length}건):`);
  for (const issue of result.issues) {
    const label = typeLabels[issue.type] || "⚠️  기타";
    const loc = issue.line > 0 ? `${issue.file}:${issue.line}` : issue.file;
    console.log(`[auditor]   ${label} — ${loc}`);
    console.log(`[auditor]     ${issue.snippet}`);
  }
  console.log(`[auditor]   → AI 호출은 반드시 import { ai } from "./ai" 를 사용하세요.`);
  console.log(`[auditor]   → ai.ts는 로컬/클라우드 자동 전환을 지원합니다.`);
  console.log(`[auditor]   → fetch()로 OpenAI를 직접 호출하면 클라우드에서 과금이 안 됩니다.\n`);
}
